import { useMemo, useState } from "react";
import { IconUpload } from "@tabler/icons-react";
import Card from "../../UI/Card";
import Text from "../../UI/Text";
import Title from "../../UI/Title";
import Button from "../../UI/Button";
import InlineEditableText from "../../UI/InlineEditableText";
import { Table } from "../../UI/Table";
import useTableSort from "../../../hooks/use-table-sort";
// @ts-expect-error pure js
import ToggleSwitch from "../../UI/ToggleSwitch";
// @ts-expect-error pure js
import useTenants from "../../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../../hooks/use-properties";
// @ts-expect-error pure js
import { useAllTenantsBalances } from "../../../hooks/use-balances";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../api/Functions";
// @ts-expect-error pure js
import { TenantStatus } from "../../../models/tenant_old";
// @ts-expect-error pure js
import { handleAPIError } from "../../../utils";
import { useToast } from "../../UI/Toast/useToast";
import TenantPropertyCell from "../TenantPropertyCell";
import CafStatementImportDialog from "./CafStatementImportDialog";
import type { Tenant } from "../../../models/tenant";
import type { Property } from "../../../models/property";
import styles from "./AplManagementTab.module.css";

type SortKey = "nom" | "caf_aide" | "montant_caf" | "caf_numero_allocataire";

export default function AplManagementTab({ tenants }: { tenants: Tenant[] }) {
  const toast = useToast();
  const [importOpen, setImportOpen] = useState(false);
  const { updateTenant, mutate: refreshTenants } = useTenants({
    status: TenantStatus.Actif.status,
  }) as {
    updateTenant: (id: number, values: Partial<Tenant>) => Promise<void>;
    mutate: () => Promise<void>;
  };
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property>;
  };
  const { mutate: refreshBalances } = useAllTenantsBalances(false, {
    groupBy: { attr: "id_tenant", unique: true },
  }) as { mutate: () => Promise<void> };
  const { sort, toggleSort, directionFor } =
    useTableSort<SortKey>("aplShow_sort");

  const displayedTenants = useMemo(() => {
    if (!sort) return tenants;
    const dir = sort.direction === "asc" ? 1 : -1;
    const compareText = (a?: string | null, b?: string | null) =>
      (a ?? "").localeCompare(b ?? "", "fr", { sensitivity: "base" });
    return [...tenants].sort((a, b) => {
      if (sort.key === "caf_aide") {
        return dir * (Number(!!a.caf_aide) - Number(!!b.caf_aide));
      }
      if (sort.key === "montant_caf") {
        return dir * ((a.montant_caf ?? 0) - (b.montant_caf ?? 0));
      }
      if (sort.key === "caf_numero_allocataire") {
        return (
          dir * compareText(a.caf_numero_allocataire, b.caf_numero_allocataire)
        );
      }
      return dir * compareText(a.nom, b.nom);
    });
  }, [tenants, sort]);

  async function save(tenantId: number, values: Partial<Tenant>) {
    try {
      await updateTenant(tenantId, values);
      return true;
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer l'aide au logement",
        description: handleAPIError(error),
      });
      return false;
    }
  }

  function renderToggle(tenant: Tenant) {
    return (
      <ToggleSwitch
        status={!!tenant.caf_aide}
        onToggle={(e: React.ChangeEvent<HTMLInputElement>) =>
          save(tenant.id, {
            caf_aide: e.target.checked,
            caf_deduction: e.target.checked,
          })
        }
        requiresWrite
        profileId={propertiesById[tenant.id_bien]?.id_profile_nom_owner}
      />
    );
  }

  function renderMontantInput(tenant: Tenant) {
    return (
      <InlineEditableText
        label="Montant des APL"
        type="number"
        step="0.01"
        unit="€"
        placeholder="250"
        value={tenant.montant_caf}
        format={(value) => currencyFormatter(Number(value))}
        disabled={!tenant.caf_aide}
        onSave={(value) =>
          save(tenant.id, {
            montant_caf: value === "" ? null : parseFloat(value),
          } as Partial<Tenant>)
        }
        requiresWrite
        profileId={propertiesById[tenant.id_bien]?.id_profile_nom_owner}
      />
    );
  }

  function renderNumeroInput(tenant: Tenant) {
    return (
      <InlineEditableText
        label="N° allocataire CAF/MSA"
        placeholder="1234567A"
        value={tenant.caf_numero_allocataire}
        disabled={!tenant.caf_aide}
        onSave={(value) =>
          save(tenant.id, { caf_numero_allocataire: value.trim() })
        }
        requiresWrite
        profileId={propertiesById[tenant.id_bien]?.id_profile_nom_owner}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <CafStatementImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => {
          refreshBalances();
          // L'import synchronise montant CAF / n° allocataire / toggle côté back
          refreshTenants();
        }}
      />

      <Card padding="md">
        <div className={styles.importCard}>
          <div className={styles.cardHeader}>
            <Title variant="deca" weight="bold">
              Relevé CAF
            </Title>
            <Text>
              Importez le bordereau de paiement reçu de la CAF : les locataires
              concernés sont détectés automatiquement et chaque montant est
              enregistré en « Paiement CAF ».
            </Text>
          </div>
          <Button icon={IconUpload} onClick={() => setImportOpen(true)}>
            Importer un relevé CAF
          </Button>
        </div>
      </Card>

      <div className={styles.tableView}>
        <Table variant="card">
          <Table.Header>
            <Table.Column
              sortDirection={directionFor("nom")}
              onSort={() => toggleSort("nom")}
            >
              Locataire
            </Table.Column>
            <Table.Column
              sortDirection={directionFor("caf_aide")}
              onSort={() => toggleSort("caf_aide")}
            >
              Le bailleur perçoit la CAF
            </Table.Column>
            <Table.Column
              sortDirection={directionFor("montant_caf")}
              onSort={() => toggleSort("montant_caf")}
            >
              Montant des APL
            </Table.Column>
            <Table.Column
              sortDirection={directionFor("caf_numero_allocataire")}
              onSort={() => toggleSort("caf_numero_allocataire")}
            >
              N° allocataire CAF/MSA
            </Table.Column>
          </Table.Header>
          <Table.Body>
            {displayedTenants.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4}>
                  <Text color="muted">Aucun locataire</Text>
                </Table.Cell>
              </Table.Row>
            )}
            {displayedTenants.map((tenant) => (
              <Table.Row key={tenant.id}>
                <Table.Cell>
                  <TenantPropertyCell
                    tenant={tenant}
                    property={propertiesById[tenant.id_bien]}
                  />
                </Table.Cell>
                <Table.Cell>{renderToggle(tenant)}</Table.Cell>
                <Table.Cell className={styles.inputCell}>
                  {renderMontantInput(tenant)}
                </Table.Cell>
                <Table.Cell className={styles.inputCell}>
                  {renderNumeroInput(tenant)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      <div className={styles.cardView}>
        {displayedTenants.length === 0 && (
          <Card padding="md">
            <Text color="muted">Aucun locataire</Text>
          </Card>
        )}
        {displayedTenants.map((tenant) => (
          <Card key={tenant.id} padding="sm" className={styles.mobileCard}>
            <div className={styles.mobileHeader}>
              <TenantPropertyCell
                tenant={tenant}
                property={propertiesById[tenant.id_bien]}
              />
              {renderToggle(tenant)}
            </div>
            {tenant.caf_aide && (
              <div className={styles.mobileFields}>
                <div>
                  <Text variant="deci">Montant des APL</Text>
                  {renderMontantInput(tenant)}
                </div>
                <div>
                  <Text variant="deci">N° allocataire CAF/MSA</Text>
                  {renderNumeroInput(tenant)}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
